// Reads and writes of the what-if sandbox (spec 03 §3.9, §4.10, §5 rows 80 to
// 85 and row 61 on a sandbox id; D-471, D-472), all through the generated
// client. Every queryFn passes its signal and every write carries a fresh
// Idempotency-Key.

import { useCallback } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import type { ScheduleBoard, ScheduleOrderLabel } from "./schedule-queries";

type S = components["schemas"];
export type ScenarioHeader = S["ScenarioHeader"];
export type ScenarioList = S["ScenarioListResponse"];
export type ScenarioStatus = ScenarioHeader["status"];
export type ScenarioError = NonNullable<ScenarioHeader["error"]>;
export type ScenarioHypothesis = S["ScenarioHypothesis"];
export type ScenarioHypothesisInput = S["ScenarioHypothesisInput"];
export type ScenarioHypothesisKind = ScenarioHeader["hypothesis_kinds"][number];
// `openapi/v1.json` writes a shift's nullable `label` as an `allOf` with an
// empty object; the contract's zod and the wire say null, so this puts the
// null back, as `schedule-queries.ts` does for the board's header.
export type ScenarioOrderShift = Omit<S["ScenarioOrderShift"], "label"> & {
  label: ScheduleOrderLabel | null;
};
export type ScenarioKpiDiff = Omit<
  NonNullable<S["ScenarioKpiDiff"]>,
  "slipped_orders" | "recovered_orders"
> & {
  slipped_orders: ScenarioOrderShift[];
  recovered_orders: ScenarioOrderShift[];
};
export type Scenario = Omit<S["Scenario"], "kpi_diff"> & { kpi_diff: ScenarioKpiDiff | null };
export type ScenarioCostImpact = S["ScenarioCostImpact"];
export type ScenarioAppliedChanges = S["ScenarioAppliedChanges"];
export type ScenarioCreateRequest = S["ScenarioCreateRequest"];
export type ScenarioPromoteResult = Omit<S["ScenarioPromoteResult"], "scenario"> & {
  scenario: Scenario;
};
type ScenarioListQuery = NonNullable<paths["/v1/scenarios"]["get"]["parameters"]["query"]>;

export const SCENARIO_STATUSES: readonly ScenarioStatus[] = [
  "open",
  "solved",
  "failed",
  "promoted",
  "discarded",
];

export const SCENARIO_ERRORS: readonly ScenarioError[] = [
  "NO_PUBLISHED_SCHEDULE",
  "TOO_MANY_OPERATIONS",
  "COMPUTE_FAILED",
  "LEASE_EXPIRED",
  "SHUTDOWN",
];

export const HYPOTHESIS_KINDS: readonly ScenarioHypothesisKind[] = [
  "machine_down",
  "add_shift",
  "add_order",
  "change_efficiency",
  "drop_order",
];

/** The kinds promote writes to the live calendar (D-472 ruling 3). */
export const PERSISTABLE_KINDS: ReadonlySet<ScenarioHypothesisKind> = new Set([
  "add_shift",
  "change_efficiency",
]);

/** At most this many hypotheses in one scenario (§2.21). */
export const SCENARIO_MAX_HYPOTHESES = 20;
/** The longest scenario name (contracts `ScenarioCreateRequest.name`). */
export const SCENARIO_NAME_MAX = 120;
/** A hypothesis window is at most this many days long. */
export const SCENARIO_MAX_WINDOW_DAYS = 92;
/** The list's page size. */
export const SCENARIO_PAGE = 50;
/** How often the result re-reads a scenario the worker has not solved yet. */
export const SCENARIO_POLL_MS = 2_000;
/** Pages of 200 operations the result reads at most, the solve's own 5 000 cap. */
const SANDBOX_PAGE = 200;
const SANDBOX_MAX_PAGES = 26;

export const scenarioKeys = {
  all: (facilityId: string) => ["planning", "scenarios", facilityId] as const,
  list: (facilityId: string, status: ScenarioStatus | undefined, cursor: string | undefined) =>
    ["planning", "scenarios", facilityId, "list", status ?? null, cursor ?? null] as const,
  one: (id: string) => ["planning", "scenario", id] as const,
  sandbox: (id: string, from: string, to: string) =>
    ["planning", "scenario-sandbox", id, from, to] as const,
  orders: (facilityId: string) => ["planning", "scenario-orders", facilityId] as const,
  order: (id: string) => ["planning", "scenario-order", id] as const,
};

/** The query row 81 takes. */
export function scenarioListQuery(
  facilityId: string,
  status: ScenarioStatus | undefined,
  cursor: string | undefined,
): ScenarioListQuery {
  const query: ScenarioListQuery = { facility_id: facilityId, limit: SCENARIO_PAGE };
  if (status !== undefined) query.status = status;
  if (cursor !== undefined) query.cursor = cursor;
  return query;
}

/**
 * One page of the facility's scenarios, newest first (row 81). It re-asks
 * every few seconds while a row on the page is still `open`, so a solve that
 * finishes moves its chip without a reload.
 */
export function useScenarios(
  facilityId: string,
  status: ScenarioStatus | undefined,
  cursor: string | undefined,
) {
  return useQuery({
    queryKey: scenarioKeys.list(facilityId, status, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((s) => s.status === "open") ? SCENARIO_POLL_MS : false,
    queryFn: async ({ signal }: ReadContext): Promise<ScenarioList> =>
      unwrap(
        await client.GET("/v1/scenarios", {
          signal,
          params: { query: scenarioListQuery(facilityId, status, cursor) },
        }),
        "scenarios",
      ),
  });
}

/**
 * One scenario (row 82). The worker solves an `open` scenario within seconds,
 * so the read re-asks every two seconds until it leaves `open`.
 */
export function useScenario(id: string) {
  return useQuery({
    queryKey: scenarioKeys.one(id),
    enabled: id.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) => (query.state.data?.status === "open" ? SCENARIO_POLL_MS : false),
    queryFn: async ({ signal }: ReadContext): Promise<Scenario> =>
      unwrap(
        await client.GET("/v1/scenarios/{id}", { signal, params: { path: { id } } }),
        "scenario",
      ) as Scenario,
  });
}

/**
 * Every page of the sandbox's board over a window (row 61 on the sandbox id;
 * D-471). The result draws its lanes from here and never calls row 65.
 */
export function useSandboxBoard(
  sandboxId: string | null,
  window: { from: string; to: string } | undefined,
) {
  return useQuery({
    queryKey: scenarioKeys.sandbox(sandboxId ?? "", window?.from ?? "", window?.to ?? ""),
    enabled: sandboxId !== null,
    retry: shouldRetryRead,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: ReadContext): Promise<ScheduleBoard> => {
      const id = sandboxId ?? "";
      const read = async (cursor: string | undefined): Promise<ScheduleBoard> =>
        unwrap(
          await client.GET("/v1/schedules/{id}", {
            signal,
            params: {
              path: { id },
              query: {
                ...(window !== undefined ? { from: window.from, to: window.to } : {}),
                limit: SANDBOX_PAGE,
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "sandbox schedule",
        );
      const first = await read(undefined);
      let operations = [...first.operations];
      let next = first.next_cursor;
      for (let page = 1; next !== null && page < SANDBOX_MAX_PAGES; page++) {
        const more = await read(next);
        operations = operations.concat(more.operations);
        next = more.next_cursor;
      }
      return { ...first, operations, next_cursor: next };
    },
  });
}

/** The statuses whose orders a `drop_order` may name: work the schedule still holds. */
const DROPPABLE_ORDER_STATUSES = ["confirmed", "in_production"] as const;
/** At most this many orders of each status in the drop-order picker, newest first. */
export const SCENARIO_ORDER_CHOICES = 50;

/** The facility's confirmed and in-production orders, for the drop-order picker. */
export function useScenarioOrders(facilityId: string, enabled: boolean) {
  return useQuery({
    queryKey: scenarioKeys.orders(facilityId),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    staleTime: 60_000,
    queryFn: async ({ signal }: ReadContext) => {
      const pages = await Promise.all(
        DROPPABLE_ORDER_STATUSES.map(async (status) =>
          unwrap(
            await client.GET("/v1/orders", {
              signal,
              params: {
                query: { facility_id: facilityId, status, limit: SCENARIO_ORDER_CHOICES },
              },
            }),
            "orders",
          ),
        ),
      );
      return pages
        .flatMap((p) => p.items)
        .sort((a, b) => (a.order_no < b.order_no ? -1 : a.order_no > b.order_no ? 1 : 0));
    },
  });
}

/** One order with its lines, for the drop-order picker's line choice. */
export function useScenarioOrder(id: string | undefined) {
  return useQuery({
    queryKey: scenarioKeys.order(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext) =>
      unwrap(
        await client.GET("/v1/orders/{id}", { signal, params: { path: { id: id ?? "" } } }),
        "order",
      ),
  });
}

// ---- writes ---------------------------------------------------------------------

/** Row 80: a scenario the worker solves; answers 202 with its id. */
export async function createScenario(body: ScenarioCreateRequest) {
  return unwrap(
    await client.POST("/v1/scenarios", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "scenario",
  );
}

/** Row 83: solve again on the current published schedule. */
export async function solveScenario(id: string): Promise<Scenario> {
  return unwrap(
    await client.POST("/v1/scenarios/{id}/solve", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "scenario solve",
  );
}

/** Row 84: write the persistable hypotheses to the live calendar. */
export async function promoteScenario(id: string): Promise<ScenarioPromoteResult> {
  return unwrap(
    await client.POST("/v1/scenarios/{id}/promote", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "scenario promote",
  );
}

/** Row 85: discard the scenario and its sandbox. */
export async function discardScenario(id: string): Promise<Scenario> {
  return unwrap(
    await client.POST("/v1/scenarios/{id}/discard", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "scenario discard",
  );
}

/** Re-reads the facility's scenario list, and the named scenarios, after a write. */
export function useScenarioRefresh() {
  const qc = useQueryClient();
  return useCallback(
    async (facilityId: string, ids: readonly string[] = []) => {
      await Promise.all([
        refreshQuery(qc, scenarioKeys.all(facilityId)),
        ...ids.map((id) => refreshQuery(qc, scenarioKeys.one(id))),
      ]);
    },
    [qc],
  );
}
